import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '../../services/apiClient';
import { useFetch } from '../../hooks/useFetch';
import { useMultiFetch } from '../../hooks/useMultiFetch';
import Button from '../ui/Button';
import DataTable from '../ui/DataTable';
import EmptyState from '../ui/EmptyState';
import { StatusPill } from '../ui/Status';
import DirectorModal from './DirectorModal';
import DirectorPanelState from './DirectorPanelState';
import PerformanceContextBar from './PerformanceContextBar';
import PerformanceStatusSummaryStrip from './PerformanceStatusSummaryStrip';
import PerformanceWorkflowStrip from './PerformanceWorkflowStrip';
import {
  buildActualScorecardRows,
  buildGoalScorecardRows,
  createDefaultPerformanceContext,
  derivePeriodStartFromContext,
  formatMetricValue,
  getPerformanceRowStatus,
  normalizePeriodStartValue,
  summarizePerformanceLifecycleCounts
} from './performanceRows';

import {
  DIRECTOR_DATA_UPDATED_EVENT,
  notifyDirectorAdminToast,
  notifyDirectorDataUpdated
} from '../../utils/directorEvents';
import { buildQuery } from '../../utils/buildQuery';
import { groupRowsByLocation, hasMultipleLocationGroups } from '../../utils/locationGrouping';

const BASELINE_TEMPLATE_NOTE_PREFIX = 'baseline template loaded';
const DIRECTOR_LOCATION_TOTAL_ID_PREFIX = '__director_location_total__';

function isGlobalDirectorManager(manager) {
  return String(manager?.managerRoleKey || '').toLowerCase() === 'director';
}

function sortGoals(goalRows) {
  return [...goalRows].sort((a, b) => {
    const aKey = `${a.goalYear}-${String(a.goalQuarter || 0).padStart(2, '0')}-${String(a.goalMonth || 0).padStart(2, '0')}-${String(a.goalWeek || 0).padStart(2, '0')}-${a.locationCode}-${a.kpiKey}`;
    const bKey = `${b.goalYear}-${String(b.goalQuarter || 0).padStart(2, '0')}-${String(b.goalMonth || 0).padStart(2, '0')}-${String(b.goalWeek || 0).padStart(2, '0')}-${b.locationCode}-${b.kpiKey}`;
    return bKey.localeCompare(aKey);
  });
}

function formatGoalWindow(goal) {
  const suffixParts = [];
  if (goal.goalQuarter) {
    suffixParts.push(`Q${goal.goalQuarter}`);
  }
  if (goal.goalMonth) {
    suffixParts.push(`M${goal.goalMonth}`);
  }
  if (goal.goalWeek) {
    suffixParts.push(`W${goal.goalWeek}`);
  }

  return `${goal.goalPeriodType} ${goal.goalYear}${suffixParts.length ? ` - ${suffixParts.join(' / ')}` : ''}`;
}

function getGoalLifecycleBadge(goal) {
  const status = String(goal?.goalStatus || 'active').toLowerCase();

  if (status === 'active') {
    return { label: 'Active', tone: 'active', detail: 'This goal is live in the current operating plan.' };
  }

  if (status === 'approved') {
    return { label: 'Approved', tone: 'active', detail: 'This proposal was approved and is now official.' };
  }

  if (status === 'pending') {
    return { label: 'Pending Approval', tone: 'inactive', detail: 'This proposal is waiting for final review.' };
  }

  if (status === 'returned') {
    return { label: 'Returned for Revision', tone: 'inactive', detail: 'This proposal needs updates before it can be approved.' };
  }

  if (status === 'rejected') {
    return { label: 'Rejected', tone: 'inactive', detail: 'This proposal was rejected and is not active.' };
  }

  return { label: 'Draft', tone: 'inactive', detail: 'This proposal is still editable and has not been submitted.' };
}

const ALL_DIRECTOR_LOCATIONS = 'ALL';

function getScorecardCopy(periodType) {
  if (periodType === 'yearly') {
    return {
      title: 'Yearly KPI Targets',
      description: 'Set strategic yearly goals for the selected scorecard.',
      targetColumn: 'Yearly Goal',
      actualColumn: 'YTD Actual'
    };
  }

  return {
    title: 'Monthly KPI Targets',
    description: 'Set operational monthly targets for the selected scorecard.',
    targetColumn: 'Monthly Target',
    actualColumn: 'MTD Actual'
  };
}

function DirectorGoalsPanel({
  readOnly = false,
  selectedLocationCode = ALL_DIRECTOR_LOCATIONS,
  locationLabel = 'Company Rollup',
  performanceContext,
  onPerformanceContextChange,
  selectedManagerIdExternal = '',
  selectedKpiKey = '',
  onSelectKpi,
  onUpdateSelectedKpiSnapshot,
  goalNavigationRequest = null,
  onConsumeGoalNavigationRequest
}) {
  const context = performanceContext || createDefaultPerformanceContext();
  const [goals, setGoals] = useState([]);
  const [locations, setLocations] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [search, setSearch] = useState('');
  const [editingGoalKpiKey, setEditingGoalKpiKey] = useState('');
  const [draftTargets, setDraftTargets] = useState({});
  const [savingRowKey, setSavingRowKey] = useState('');
  const [historyGoalId, setHistoryGoalId] = useState(null);
  const [banner, setBanner] = useState('');
  const [error, setError] = useState('');
  const effectiveLocationCode = selectedLocationCode !== ALL_DIRECTOR_LOCATIONS ? selectedLocationCode : '';
  const hasExplicitLocationContext = Boolean(context.locationCode || effectiveLocationCode);
  const effectiveSelectedManagerId = useMemo(() => {
    if (!selectedManagerIdExternal) {
      return '';
    }

    const normalizedValue = String(selectedManagerIdExternal);
    if (normalizedValue.startsWith(DIRECTOR_LOCATION_TOTAL_ID_PREFIX)) {
      return '';
    }

    return normalizedValue;
  }, [selectedManagerIdExternal]);

  const periodStart = useMemo(() => derivePeriodStartFromContext(context), [context]);

  const filteredGoals = useMemo(() => goals.filter(goal => {
    if ((context.locationCode || effectiveLocationCode) && goal.locationCode !== (context.locationCode || effectiveLocationCode)) {
      return false;
    }
    if (goal.goalPeriodType !== context.periodType) {
      return false;
    }
    if (normalizePeriodStartValue(goal.periodStart) !== normalizePeriodStartValue(periodStart)) {
      return false;
    }
    return true;
  }), [context.locationCode, context.periodType, effectiveLocationCode, goals, periodStart]);

  const scopedLocations = useMemo(
    () => (effectiveLocationCode
      ? locations.filter(location => location.isActive && location.code === effectiveLocationCode)
      : locations.filter(location => location.isActive)),
    [effectiveLocationCode, locations]
  );

  const goalLocationCode = effectiveLocationCode || context.locationCode || '';
  const goalQuery = useMemo(() => {
    const qs = buildQuery({
      locationCode: goalLocationCode,
      periodType: context.periodType,
      periodStart
    });
    return qs ? `?${qs}` : '';
  }, [goalLocationCode, context.periodType, periodStart]);

  const baseUrls = useMemo(() => ({
    goals: `/api/goals/location${goalQuery}`,
    locations: '/api/locations',
    kpis: '/api/kpis',
    managers: '/api/managers'
  }), [goalQuery]);

  const {
    data: fetchedBase,
    isLoading: isBaseLoading,
    error: baseError,
    refetch: refetchBase
  } = useMultiFetch(baseUrls, { deps: [goalQuery] });

  useEffect(() => {
    if (fetchedBase?.goals) {
      setGoals(sortGoals(fetchedBase.goals));
    }
    if (fetchedBase?.locations) {
      setLocations(fetchedBase.locations);
    }
    if (fetchedBase?.kpis) {
      setKpis(fetchedBase.kpis);
    }
    if (fetchedBase) {
      setError('');
    }
    if (baseError) {
      setError(baseError);
    }
  }, [fetchedBase, baseError]);

  const scopedManagerRecord = useMemo(() => {
    const managerRows = fetchedBase?.managers || [];
    if (!managerRows.length) {
      return null;
    }
    const scopedLocationManagers = goalLocationCode
      ? managerRows.filter((manager) => String(manager.locationCode || '').toUpperCase() === String(goalLocationCode || '').toUpperCase())
      : managerRows;
    const preferredScopedManagers = scopedLocationManagers.filter((manager) => !isGlobalDirectorManager(manager));
    const defaultScopedManager = preferredScopedManagers[0] || scopedLocationManagers[0] || null;
    if (effectiveSelectedManagerId) {
      return managerRows.find((manager) => (
        String(manager.id) === String(effectiveSelectedManagerId)
        || String(manager.managerKey || '') === String(effectiveSelectedManagerId)
      )) || defaultScopedManager;
    }
    return defaultScopedManager;
  }, [fetchedBase, goalLocationCode, effectiveSelectedManagerId]);

  const actualQueryManagerId = scopedManagerRecord?.id || null;
  const referenceManagerId = actualQueryManagerId ? String(actualQueryManagerId) : '';
  const referenceManagerName = scopedManagerRecord
    ? `${scopedManagerRecord.firstName || ''} ${scopedManagerRecord.lastName || ''}`.trim()
    : '';

  const exactActualsUrl = useMemo(() => {
    if (!actualQueryManagerId && !goalLocationCode) {
      return null;
    }
    const qs = buildQuery({
      managerId: actualQueryManagerId || null,
      locationCode: actualQueryManagerId ? null : goalLocationCode,
      periodType: context.periodType,
      periodStart
    });
    return `/api/actuals?${qs}`;
  }, [actualQueryManagerId, goalLocationCode, context.periodType, periodStart]);

  const fallbackActualsUrl = useMemo(() => {
    if (context.periodType !== 'yearly') {
      return null;
    }
    if (!actualQueryManagerId && !goalLocationCode) {
      return null;
    }
    const qs = buildQuery({
      managerId: actualQueryManagerId || null,
      locationCode: actualQueryManagerId ? null : goalLocationCode,
      periodType: 'monthly',
      actualYear: Number(context.year)
    });
    return `/api/actuals?${qs}`;
  }, [actualQueryManagerId, goalLocationCode, context.periodType, context.year]);

  const {
    data: exactActualRows,
    error: exactActualsError,
    refetch: refetchExactActuals
  } = useFetch(exactActualsUrl, { initialData: [] });

  const {
    data: fallbackActualRows,
    error: fallbackActualsError,
    refetch: refetchFallbackActuals
  } = useFetch(fallbackActualsUrl, { initialData: [] });

  const actuals = useMemo(() => {
    const exact = Array.isArray(exactActualRows) ? exactActualRows : [];
    if (context.periodType !== 'yearly') {
      return exact;
    }
    const fallback = Array.isArray(fallbackActualRows) ? fallbackActualRows : [];
    const exactKeys = new Set(exact.map((actual) => `${actual.managerId}:${actual.kpiDefinitionId}`));
    return [
      ...exact,
      ...fallback.filter((actual) => !exactKeys.has(`${actual.managerId}:${actual.kpiDefinitionId}`))
    ];
  }, [exactActualRows, fallbackActualRows, context.periodType]);

  useEffect(() => {
    if (exactActualsError) {
      setError(exactActualsError);
    } else if (fallbackActualsError) {
      setError(fallbackActualsError);
    }
  }, [exactActualsError, fallbackActualsError]);

  const trackerUrl = useMemo(() => {
    if (!periodStart) {
      return null;
    }
    if (referenceManagerId) {
      const qs = buildQuery({ periodType: context.periodType, periodStart });
      return `/api/tracker/manager/${encodeURIComponent(referenceManagerId)}?${qs}`;
    }
    if (!goalLocationCode) {
      return null;
    }
    const qs = buildQuery({
      periodType: context.periodType,
      periodStart,
      locationCode: goalLocationCode
    });
    return `/api/tracker/location?${qs}`;
  }, [context.periodType, goalLocationCode, periodStart, referenceManagerId]);

  const {
    data: trackerPayload,
    isLoading: isTrackerLoading,
    error: trackerError,
    refetch: refetchTracker
  } = useFetch(trackerUrl);

  const trackerMetrics = useMemo(() => trackerPayload?.metrics || [], [trackerPayload]);

  useEffect(() => {
    if (trackerError) {
      setError(trackerError);
    }
  }, [trackerError]);

  const isLoading = isBaseLoading || isTrackerLoading;

  const historyUrl = historyGoalId ? `/api/goals/location/${historyGoalId}/history` : null;
  const { data: historyData, error: historyError } = useFetch(historyUrl, { initialData: [] });
  const goalHistory = useMemo(() => (Array.isArray(historyData) ? historyData : []), [historyData]);

  useEffect(() => {
    if (historyError) {
      setError(historyError);
    }
  }, [historyError]);

  const refetchAll = useCallback(() => {
    refetchBase();
    refetchExactActuals();
    refetchFallbackActuals();
    refetchTracker();
  }, [refetchBase, refetchExactActuals, refetchFallbackActuals, refetchTracker]);

  useEffect(() => {
    const handleDirectorDataUpdated = () => refetchAll();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
    return () => {
      window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handleDirectorDataUpdated);
    };
  }, [refetchAll]);

  const actualReferenceRows = useMemo(() => buildActualScorecardRows({
    kpis,
    goals: filteredGoals,
    actuals,
    trackerMetrics,
    managerName: referenceManagerName,
    context
  }), [actuals, context, filteredGoals, kpis, referenceManagerName, trackerMetrics]);

  const scorecardRows = useMemo(() => {
    if (!hasExplicitLocationContext) {
      return [];
    }

    const rows = buildGoalScorecardRows({
      kpis,
      goals: filteredGoals,
      trackerMetrics,
      actuals,
      context: {
        locationCode: context.locationCode || effectiveLocationCode,
        periodType: context.periodType
      }
    });
    const actualReferenceMap = new Map(actualReferenceRows.map((row) => [row.kpiKey, row]));
    const mergedRows = rows.map((row) => {
      const actualReferenceRow = actualReferenceMap.get(row.kpiKey);
      if (!actualReferenceRow) {
        return row;
      }

      return {
        ...row,
        actual: Number.isFinite(actualReferenceRow.actual) ? actualReferenceRow.actual : row.actual,
        variance: Number.isFinite(actualReferenceRow.variance) ? actualReferenceRow.variance : row.variance
      };
    });

    const searchValue = search.trim().toLowerCase();
    if (!searchValue) {
      return mergedRows;
    }

    return mergedRows.filter(row => (
      row.category.toLowerCase().includes(searchValue)
      || row.goalName.toLowerCase().includes(searchValue)
      || row.kpiKey.toLowerCase().includes(searchValue)
      || row.kpiLabel.toLowerCase().includes(searchValue)
    ));
  }, [actualReferenceRows, actuals, context.locationCode, context.periodType, effectiveLocationCode, filteredGoals, hasExplicitLocationContext, kpis, search, trackerMetrics]);

  const statusCounts = useMemo(
    () => summarizePerformanceLifecycleCounts(scorecardRows, 'goals'),
    [scorecardRows]
  );
  const scorecardCopy = useMemo(
    () => getScorecardCopy(context.periodType),
    [context.periodType]
  );
  const baselineDraftCount = useMemo(
    () => filteredGoals.filter((goal) => (
      String(goal.goalStatus || '').toLowerCase() === 'draft'
      && String(goal.statusNote || '').toLowerCase().startsWith(BASELINE_TEMPLATE_NOTE_PREFIX)
    )).length,
    [filteredGoals]
  );
  const goalLocationGroups = useMemo(
    () => groupRowsByLocation(filteredGoals),
    [filteredGoals]
  );
  const showGoalLocationHeaders = hasMultipleLocationGroups(goalLocationGroups);

  const editingGoalRow = useMemo(
    () => scorecardRows.find(row => row.kpiKey === editingGoalKpiKey) || null,
    [editingGoalKpiKey, scorecardRows]
  );

  const buildSelectedKpiSnapshot = useCallback((row) => {
    const draftValue = draftTargets[row.kpiKey];
    const persistedValue = Number.isFinite(row.target) ? String(row.target) : '';
    const normalizedDraft = draftValue === undefined ? persistedValue : String(draftValue);
    const parsedDraft = normalizedDraft === '' ? null : Number(normalizedDraft);
    const isDirty = normalizedDraft !== persistedValue;
    const status = getPerformanceRowStatus('goals', row, { isDirty });

    return {
      stage: 'goals',
      kpiKey: row.kpiKey,
      kpiLabel: row.kpiLabel,
      category: row.category,
      goalName: row.goalName,
      statusLabel: status.label,
      statusTone: status.tone,
      statusDetail: status.detail,
      target: Number.isFinite(parsedDraft) ? parsedDraft : row.target,
      actual: row.actual,
      variance: row.variance,
      unit: row.unit,
      formatType: row.formatType,
      rowStateLabel: row.isLocked ? 'Locked' : (isDirty ? 'Unsaved Change' : 'Open')
    };
  }, [draftTargets]);

  useEffect(() => {
    if (!selectedKpiKey) {
      return;
    }

    const selectedRow = scorecardRows.find(row => row.kpiKey === selectedKpiKey);
    if (!selectedRow) {
      onUpdateSelectedKpiSnapshot?.({
        stage: 'goals',
        kpiKey: selectedKpiKey,
        clear: true
      });
      return;
    }

    const snapshot = buildSelectedKpiSnapshot(selectedRow);
    onSelectKpi?.(snapshot);
    onUpdateSelectedKpiSnapshot?.(snapshot);
  }, [buildSelectedKpiSnapshot, onSelectKpi, onUpdateSelectedKpiSnapshot, scorecardRows, selectedKpiKey]);

  useEffect(() => {
    if (editingGoalKpiKey && !editingGoalRow) {
      setEditingGoalKpiKey('');
    }
  }, [editingGoalKpiKey, editingGoalRow]);

  useEffect(() => {
    if (!editingGoalRow) {
      return;
    }

    window.setTimeout(() => {
      const targetInput = document.querySelector(`[data-goal-target-input="${editingGoalRow.kpiKey}"]`);
      if (targetInput && typeof targetInput.focus === 'function') {
        targetInput.focus();
        if (typeof targetInput.select === 'function') {
          targetInput.select();
        }
      }
    }, 0);
  }, [editingGoalRow]);

  useEffect(() => {
    if (!goalNavigationRequest?.kpiKey) {
      return;
    }

    const contextMatches = (
      (goalNavigationRequest.locationCode || '') === (context.locationCode || effectiveLocationCode || '')
      && (goalNavigationRequest.periodType || '') === (context.periodType || '')
      && (goalNavigationRequest.periodStart || '') === (periodStart || '')
    );

    if (!contextMatches) {
      return;
    }

    const matchingRow = scorecardRows.find(row => (
      row.kpiKey === goalNavigationRequest.kpiKey
      && (!goalNavigationRequest.goalId || row.exactGoalRecord?.id === goalNavigationRequest.goalId)
    ));

    if (!matchingRow) {
      return;
    }

    const snapshot = buildSelectedKpiSnapshot(matchingRow);
    onSelectKpi?.(snapshot);
    onUpdateSelectedKpiSnapshot?.(snapshot);

    if (Number.isFinite(goalNavigationRequest.targetValue)) {
      setDraftTargets(current => ({
        ...current,
        [matchingRow.kpiKey]: String(goalNavigationRequest.targetValue)
      }));
    }

    setEditingGoalKpiKey(matchingRow.kpiKey);

    onConsumeGoalNavigationRequest?.();
  }, [
    buildSelectedKpiSnapshot,
    context.locationCode,
    context.periodType,
    effectiveLocationCode,
    goalNavigationRequest,
    onConsumeGoalNavigationRequest,
    onSelectKpi,
    onUpdateSelectedKpiSnapshot,
    periodStart,
    scorecardRows
  ]);

  const selectedHistoryGoal = useMemo(
    () => goals.find(goal => goal.id === historyGoalId) || null,
    [goals, historyGoalId]
  );

  useEffect(() => {
    if (!effectiveLocationCode) {
      return;
    }

    if (context.locationCode !== effectiveLocationCode) {
      onPerformanceContextChange?.('locationCode', effectiveLocationCode);
    }
  }, [context.locationCode, effectiveLocationCode, onPerformanceContextChange]);

  useEffect(() => {
    if (!effectiveLocationCode && !context.locationCode && scopedLocations.length === 1 && scopedLocations[0]?.code) {
      onPerformanceContextChange?.('locationCode', scopedLocations[0].code);
    }
  }, [context.locationCode, effectiveLocationCode, onPerformanceContextChange, scopedLocations]);

  const handleContextChange = (field, value) => {
    onPerformanceContextChange?.(field, value);
  };

  const handleTargetDraftChange = (row, value) => {
    setDraftTargets(current => ({
      ...current,
      [row.kpiKey]: value
    }));
  };

  const resolveExistingGoalForRow = useCallback((row) => {
    if (row.goalRecord) {
      return row.goalRecord;
    }

    const locationCode = row.locationCode || context.locationCode || effectiveLocationCode;
    return goals.find(goal => (
      goal.kpiKey === row.kpiKey
      && goal.locationCode === locationCode
      && goal.goalPeriodType === context.periodType
      && normalizePeriodStartValue(goal.periodStart) === normalizePeriodStartValue(periodStart)
    )) || filteredGoals.find(goal => goal.kpiKey === row.kpiKey) || null;
  }, [context.locationCode, context.periodType, effectiveLocationCode, filteredGoals, goals, periodStart]);

  const getGoalDraftState = useCallback((row) => {
    const draftValue = draftTargets[row.kpiKey];
    const persistedValue = Number.isFinite(row.target) ? String(row.target) : '';
    const normalizedDraft = draftValue === undefined ? persistedValue : String(draftValue);
    const isDirty = normalizedDraft !== persistedValue;
    const existingGoal = resolveExistingGoalForRow(row);
    const hasPersistedTarget = Boolean(existingGoal);
    const buttonLabel = hasPersistedTarget
      ? (isDirty ? 'Update Target' : 'Save Target')
      : row.targetSource === 'yearly_reference'
        ? 'Add Exact Target'
      : 'Add Target';

    return {
      draftValue,
      persistedValue,
      normalizedDraft,
      isDirty,
      existingGoal,
      hasPersistedTarget,
      buttonLabel
    };
  }, [draftTargets, resolveExistingGoalForRow]);

  const handleSaveGoalRow = async (row) => {
    setBanner('');
    setError('');
    setSavingRowKey(row.kpiKey);

    try {
      const nextTarget = Number(draftTargets[row.kpiKey] ?? row.target);
      const existingGoal = resolveExistingGoalForRow(row);
      const locationCode = context.locationCode || effectiveLocationCode;
      const requestBody = JSON.stringify({
        locationCode,
        kpiKey: row.kpiKey,
        goalPeriodType: context.periodType,
        goalYear: Number(context.year),
        goalMonth: context.periodType === 'monthly' ? Number(context.month) : null,
        goalQuarter: context.periodType === 'quarterly' ? Number(context.quarter) : null,
        goalWeek: context.periodType === 'weekly' ? Number(context.week) : null,
        periodStart,
        goalValue: nextTarget
      });

      let savedGoal;
      if (existingGoal) {
        savedGoal = await apiRequest(`/api/goals/location/${existingGoal.id}`, {
          method: 'PATCH',
          body: requestBody
        });
      } else {
        try {
          savedGoal = await apiRequest('/api/goals/location', {
            method: 'POST',
            body: requestBody
          });
        } catch (requestError) {
          const isDuplicateGoal = String(requestError?.message || '').toLowerCase().includes('duplicate key value');
          if (!isDuplicateGoal) {
            throw requestError;
          }

          const refreshQs = buildQuery({
            locationCode,
            periodType: context.periodType,
            periodStart
          });
          const refreshedGoals = await apiRequest(`/api/goals/location?${refreshQs}`);
          const refreshedGoal = refreshedGoals.find(goal => goal.kpiKey === row.kpiKey);

          if (!refreshedGoal) {
            throw requestError;
          }

          savedGoal = await apiRequest(`/api/goals/location/${refreshedGoal.id}`, {
            method: 'PATCH',
            body: requestBody
          });
        }
      }

      setGoals(current => sortGoals(
        existingGoal
          ? current.map(goal => (goal.id === savedGoal.id ? savedGoal : goal))
          : [savedGoal, ...current]
      ));
      setDraftTargets(current => {
        const next = { ...current };
        delete next[row.kpiKey];
        return next;
      });
      setBanner(existingGoal
        ? `Updated target for ${savedGoal.label}.`
        : `Saved target for ${savedGoal.label}.`);
      notifyDirectorAdminToast(
        existingGoal
          ? `Updated target for ${savedGoal.label}.`
          : `Saved target for ${savedGoal.label}.`
      );
      notifyDirectorDataUpdated('Director dashboard refreshed for goal changes.');
      refetchTracker();
      return savedGoal;
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to save goal.', 'error');
      setError(requestError.message || 'Unable to save goal.');
      return null;
    } finally {
      setSavingRowKey('');
    }
  };

  const handleSaveGoalDraftRow = async (row) => {
    setBanner('');
    setError('');
    setSavingRowKey(`${row.kpiKey}:draft`);

    try {
      const nextTarget = Number(draftTargets[row.kpiKey] ?? row.target);
      const existingGoal = resolveExistingGoalForRow(row);
      const locationCode = context.locationCode || effectiveLocationCode;
      const requestBody = JSON.stringify({
        locationCode,
        kpiKey: row.kpiKey,
        goalPeriodType: context.periodType,
        goalYear: Number(context.year),
        goalMonth: context.periodType === 'monthly' ? Number(context.month) : null,
        goalQuarter: context.periodType === 'quarterly' ? Number(context.quarter) : null,
        goalWeek: context.periodType === 'weekly' ? Number(context.week) : null,
        periodStart,
        goalValue: nextTarget
      });

      let savedGoal;
      if (existingGoal && ['draft', 'returned', 'rejected'].includes(String(existingGoal.goalStatus || '').toLowerCase())) {
        savedGoal = await apiRequest(`/api/goals/location/${existingGoal.id}/proposal`, {
          method: 'PATCH',
          body: requestBody
        });
      } else if (!existingGoal) {
        savedGoal = await apiRequest('/api/goals/location/proposal', {
          method: 'POST',
          body: requestBody
        });
      } else {
        throw new Error('Only draft, returned, or rejected goals can be saved as proposals.');
      }

      setGoals(current => sortGoals(
        existingGoal
          ? current.map(goal => (goal.id === savedGoal.id ? savedGoal : goal))
          : [savedGoal, ...current]
      ));
      setDraftTargets(current => {
        const next = { ...current };
        delete next[row.kpiKey];
        return next;
      });
      setBanner(`Saved draft proposal for ${savedGoal.label}.`);
      notifyDirectorAdminToast(`Saved draft proposal for ${savedGoal.label}.`);
      notifyDirectorDataUpdated('Director dashboard refreshed for goal changes.');
      refetchTracker();
      return savedGoal;
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to save draft goal proposal.', 'error');
      setError(requestError.message || 'Unable to save draft goal proposal.');
      return null;
    } finally {
      setSavingRowKey('');
    }
  };

  const handleSubmitGoalForApproval = async (goal) => {
    setBanner('');
    setError('');
    setSavingRowKey(`${goal.id}:submit`);

    try {
      const submittedGoal = await apiRequest(`/api/goals/location/${goal.id}/submit`, {
        method: 'POST'
      });

      setGoals(current => sortGoals(current.map(item => (item.id === submittedGoal.id ? submittedGoal : item))));
      setBanner(`Submitted ${submittedGoal.label} for approval.`);
      notifyDirectorAdminToast(`Submitted ${submittedGoal.label} for approval.`);
      notifyDirectorDataUpdated('Director dashboard refreshed for goal changes.');
      return submittedGoal;
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to submit goal for approval.', 'error');
      setError(requestError.message || 'Unable to submit goal for approval.');
      return null;
    } finally {
      setSavingRowKey('');
    }
  };

  const handleToggleLock = async (goal) => {
    setBanner('');
    setError('');

    try {
      const updated = await apiRequest(`/api/goals/location/${goal.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          isLocked: !goal.isLocked
        })
      });

      setGoals(current => sortGoals(current.map(item => (item.id === updated.id ? updated : item))));
      setBanner(updated.isLocked ? `Locked ${updated.locationCode} / ${updated.kpiKey}.` : `Unlocked ${updated.locationCode} / ${updated.kpiKey}.`);
      notifyDirectorDataUpdated('Director dashboard refreshed for goal changes.');
      notifyDirectorAdminToast(
        updated.isLocked ? `Locked ${updated.locationCode} / ${updated.kpiKey}.` : `Unlocked ${updated.locationCode} / ${updated.kpiKey}.`,
        'success',
        {
          label: 'Undo',
          onClick: async () => {
            const reverted = await apiRequest(`/api/goals/location/${updated.id}`, {
              method: 'PATCH',
              body: JSON.stringify({
                isLocked: goal.isLocked
              })
            });

            setGoals(current => sortGoals(current.map(item => (item.id === reverted.id ? reverted : item))));
            notifyDirectorDataUpdated('Director dashboard refreshed for goal changes.');
            notifyDirectorAdminToast(
              reverted.isLocked ? `Locked ${reverted.locationCode} / ${reverted.kpiKey}.` : `Unlocked ${reverted.locationCode} / ${reverted.kpiKey}.`
            );
          }
        }
      );
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to update goal lock state.', 'error');
      setError(requestError.message || 'Unable to update goal lock state.');
    }
  };

  const handleActivateGoal = async (goal) => {
    setBanner('');
    setError('');
    setSavingRowKey(`${goal.id}:activate`);

    try {
      const updated = await apiRequest(`/api/goals/location/${goal.id}/activate`, {
        method: 'POST'
      });

      setGoals(current => sortGoals(current.map(item => (item.id === updated.id ? updated : item))));
      setBanner(`Activated ${updated.label}.`);
      notifyDirectorAdminToast(`Activated ${updated.label}.`);
      notifyDirectorDataUpdated('Director dashboard refreshed for goal changes.');
      refetchTracker();
      return updated;
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to activate goal.', 'error');
      setError(requestError.message || 'Unable to activate goal.');
      return null;
    } finally {
      setSavingRowKey('');
    }
  };

  const handleRemoveGoal = async (goal) => {
    setBanner('');
    setError('');
    setSavingRowKey(`${goal.id}:remove`);

    try {
      const updated = await apiRequest(`/api/goals/location/${goal.id}/remove`, {
        method: 'POST'
      });

      setGoals(current => sortGoals(current.map(item => (item.id === updated.id ? updated : item))));
      setBanner(`Removed ${updated.label} from activation planning.`);
      notifyDirectorAdminToast(`Removed ${updated.label} from activation planning.`);
      notifyDirectorDataUpdated('Director dashboard refreshed for goal changes.');
      refetchTracker();
      return updated;
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to remove goal from plan.', 'error');
      setError(requestError.message || 'Unable to remove goal from plan.');
      return null;
    } finally {
      setSavingRowKey('');
    }
  };

  return (
    <div className="director-admin-stack">
      <DirectorPanelState kind="loading" message={isLoading ? 'Loading goal management data...' : ''} />
      <DirectorPanelState kind="error" message={error} />
      <DirectorPanelState kind="success" message={!error ? banner : ''} />

      <PerformanceWorkflowStrip activeStep="goals" />

      <PerformanceContextBar
        title="Performance Goal Context"
        context={context}
        onChange={handleContextChange}
        locations={scopedLocations}
        selectedLocationCode={selectedLocationCode}
        allLocationsValue={ALL_DIRECTOR_LOCATIONS}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search category, goal, or KPI"
      />

      <PerformanceStatusSummaryStrip counts={statusCounts} />

      <DirectorPanelState
        kind="warning"
        message={!hasExplicitLocationContext
          ? 'Select a single location to manage exact goals. All Assigned Sites can show broad context, but goal editing requires one location.'
          : ''}
      />

      <DirectorPanelState
        kind="info"
        message={readOnly
          ? 'This is a view-only goal surface for your permitted location scope. Goal editing, submission, and activation stay with the existing permissions workflow.'
          : ''}
      />

      <DirectorPanelState
        kind="info"
        message={baselineDraftCount > 0
          ? `Baseline Goals Loaded: ${baselineDraftCount} draft goal${baselineDraftCount === 1 ? ' is' : 's are'} ready for review in this location and period. Edit, remove, or activate them when ready.`
          : ''}
      />

      <section className="director-card director-scorecard-surface">
        <div className="role-section-header">
          <h2>{scorecardCopy.title}</h2>
          <span>{scorecardRows.length} KPI rows</span>
        </div>
        <div className="director-scorecard-meta">
          <span className="director-scorecard-meta-pill">Location {context.locationCode || locationLabel}</span>
          <span className="director-scorecard-meta-pill">Period Start {periodStart || 'Select a valid period'}</span>
          <span className="director-scorecard-meta-pill">{context.periodType === 'yearly' ? 'Yearly View' : 'Monthly View'}</span>
        </div>
        <p className="role-section-copy">
          Goal lifecycle: `Draft` is editable, `Pending Approval` is awaiting review, `Approved` is an official approved proposal, `Active` is a live operating goal, `Returned for Revision` needs edits, and `Rejected` is closed.
        </p>
        <DataTable
          tableClassName="director-scorecard-table"
          aria-label={`${scorecardCopy.title} table, scroll horizontally for more columns`}
          emptyMessage="No KPI rows match the current location and period context."
          emptyState={(
            <EmptyState
              icon="G"
              headline="No goals yet"
              body="Set the first target for this location and period so dashboards have a clear operating plan."
              primaryCtaLabel={readOnly ? '' : 'Add First Goal'}
              onPrimaryCta={readOnly ? null : () => {
                const firstRow = scorecardRows[0];
                if (firstRow) {
                  setEditingGoalKpiKey(firstRow.kpiKey);
                }
              }}
              compact
            />
          )}
          rows={scorecardRows}
          rowKey={(row) => row.id}
          rowLabel={(row) => row.kpiLabel}
          rowStatus={(row) => {
            const { isDirty } = getGoalDraftState(row);
            return getPerformanceRowStatus('goals', row, { isDirty }).tone;
          }}
          rowClassName={(row) => `${row.isLocked ? 'director-scorecard-row-locked' : ''} ${selectedKpiKey === row.kpiKey ? 'director-kpi-row-focused' : ''}`.trim()}
          onRowClick={(row) => {
            const snapshot = buildSelectedKpiSnapshot(row);
            onSelectKpi?.(snapshot);
            onUpdateSelectedKpiSnapshot?.(snapshot);
          }}
          groupBy={showGoalLocationHeaders ? row => row.locationCode || 'Unassigned location' : null}
          groupLabel={(group, rows) => `${group} · ${rows.length} KPI row${rows.length === 1 ? '' : 's'}`}
          columns={[
            { key: 'category', label: 'Category', render: row => row.category },
            {
              key: 'kpi',
              label: 'KPI',
              render: row => (
                <>
                  <strong>{row.kpiLabel}</strong>
                  <div className="director-muted">{row.kpiKey}</div>
                </>
              )
            },
            {
              key: 'target',
              label: scorecardCopy.targetColumn,
              render: row => {
                const { isDirty } = getGoalDraftState(row);
                const displayValue = draftTargets[row.kpiKey];
                const parsedDraft = displayValue === undefined || displayValue === '' ? null : Number(displayValue);
                const resolvedTarget = Number.isFinite(parsedDraft) ? parsedDraft : row.target;

                return (
                  <div className="director-target-meta">
                    {Number.isFinite(resolvedTarget)
                      ? <strong>{formatMetricValue(resolvedTarget, row.unit, row.formatType)}</strong>
                      : <span className="director-warning-copy director-inline-note">No target set</span>}
                    {row.isLocked ? (
                      <StatusPill tone="not-required">Locked</StatusPill>
                    ) : row.targetSource === 'yearly_reference' && !row.goalRecord ? (
                      <StatusPill tone="not-required">Yearly Reference</StatusPill>
                    ) : isDirty ? (
                      <StatusPill tone="needs-link">Unsaved Draft</StatusPill>
                    ) : (
                      <StatusPill tone="linked">Open</StatusPill>
                    )}
                  </div>
                );
              }
            },
            { key: 'actual', label: scorecardCopy.actualColumn, render: row => formatMetricValue(row.actual, row.unit, row.formatType) },
            {
              key: 'status',
              label: 'Goal state',
              render: row => {
                const { isDirty } = getGoalDraftState(row);
                const status = getPerformanceRowStatus('goals', row, { isDirty });
                const goalBadge = row.goalRecord ? getGoalLifecycleBadge(row.goalRecord) : null;
                return (
                  <div className="director-row-status">
                    <StatusPill tone={status.tone}>{status.label}</StatusPill>
                    {goalBadge ? <StatusPill tone={goalBadge.tone} title={goalBadge.detail}>{goalBadge.label}</StatusPill> : null}
                    {status.detail ? <div className="director-muted">{status.detail}</div> : null}
                  </div>
                );
              }
            },
            { key: 'goal', label: 'Goal', render: row => row.goalName },
            { key: 'variance', label: 'Variance (+/-)', render: row => formatMetricValue(row.variance, row.unit, row.formatType) },
            {
              key: 'actions',
              label: 'Actions',
              render: row => {
                if (readOnly) {
                  return (
                    <div className="director-table-actions">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={(event) => {
                          event.stopPropagation();
                          setEditingGoalKpiKey(row.kpiKey);
                        }}
                      >
                        View
                      </Button>
                    </div>
                  );
                }

                const { isDirty, existingGoal, hasPersistedTarget } = getGoalDraftState(row);
                return (
                  <div className="director-table-actions">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={(event) => {
                        event.stopPropagation();
                        setEditingGoalKpiKey(row.kpiKey);
                      }}
                    >
                      {row.isLocked ? 'View Target' : hasPersistedTarget ? (isDirty ? 'Resume Edit' : 'Edit Target') : 'Add Target'}
                    </Button>
                    {existingGoal ? (
                      <>
                        <Button
                          size="sm"
                          variant={row.isLocked ? 'secondary' : 'danger'}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleToggleLock(existingGoal);
                          }}
                        >
                          {row.isLocked ? 'Unlock' : 'Lock'}
                        </Button>
                        <Button size="sm" onClick={(event) => {
                          event.stopPropagation();
                          setHistoryGoalId(existingGoal.id);
                        }}>
                          History
                        </Button>
                      </>
                    ) : null}
                  </div>
                );
              }
            }
          ]}
        />
      </section>

      {editingGoalRow ? (
        <DirectorModal
          title={readOnly ? 'View Goal' : (editingGoalRow.isLocked ? 'View Target' : (getGoalDraftState(editingGoalRow).hasPersistedTarget ? 'Edit Target' : 'Add Target'))}
          badge="Goal Management"
          onClose={() => setEditingGoalKpiKey('')}
        >
          {(() => {
            const {
              existingGoal,
              isDirty,
              buttonLabel
            } = getGoalDraftState(editingGoalRow);
            const status = getPerformanceRowStatus('goals', editingGoalRow, { isDirty });
            const proposalEligible = existingGoal
              ? ['draft', 'returned', 'rejected'].includes(String(existingGoal.goalStatus || '').toLowerCase())
              : true;
            const submissionEligible = existingGoal
              && ['draft', 'returned', 'rejected'].includes(String(existingGoal.goalStatus || '').toLowerCase());
            const activationEligible = existingGoal
              && ['draft', 'approved'].includes(String(existingGoal.goalStatus || '').toLowerCase());
            const removalEligible = existingGoal
              && ['draft', 'approved', 'returned', 'rejected'].includes(String(existingGoal.goalStatus || '').toLowerCase());

            return (
              <>
                <div className="director-history-row">
                  <strong>{editingGoalRow.kpiLabel}</strong>
                  <span>{editingGoalRow.category} / {editingGoalRow.goalName}</span>
                </div>
                <p className="director-muted">
                  {editingGoalRow.targetSource === 'yearly_reference' && !existingGoal
                    ? 'This row is currently showing a yearly reference value. Saving here creates an exact goal for the selected location and period.'
                    : 'Update the active location target for this KPI. Actual and variance remain reference values in this editor so the save path stays focused on target management.'}
                </p>
                <div className="director-form-grid director-inline-form-grid">
                  <label>
                    Target Value
                    <input
                      className={`director-target-input ${isDirty ? 'dirty' : ''} ${editingGoalRow.isLocked ? 'locked' : ''}`}
                      type="number"
                      step="0.01"
                      data-goal-target-input={editingGoalRow.kpiKey}
                      value={draftTargets[editingGoalRow.kpiKey] ?? (Number.isFinite(editingGoalRow.target) ? editingGoalRow.target : '')}
                      onChange={(event) => handleTargetDraftChange(editingGoalRow, event.target.value)}
                      disabled={readOnly || editingGoalRow.isLocked}
                      readOnly={readOnly}
                    />
                  </label>
                  <label>
                    Actual
                    <input
                      type="text"
                      value={formatMetricValue(editingGoalRow.actual, editingGoalRow.unit, editingGoalRow.formatType)}
                      readOnly
                    />
                  </label>
                  <label>
                    Variance (+/-)
                    <input
                      type="text"
                      value={formatMetricValue(editingGoalRow.variance, editingGoalRow.unit, editingGoalRow.formatType)}
                      readOnly
                    />
                  </label>
                  <div className="director-toolbar-group">
                    <label>Goal state</label>
                    <div className="director-target-meta">
                      <StatusPill tone={status.tone}>{status.label}</StatusPill>
                      {existingGoal ? (
                        <StatusPill tone={getGoalLifecycleBadge(existingGoal).tone}>
                          {getGoalLifecycleBadge(existingGoal).label}
                        </StatusPill>
                      ) : null}
                      {existingGoal ? <span className="director-muted">{getGoalLifecycleBadge(existingGoal).detail}</span> : null}
                      {!existingGoal && editingGoalRow.targetSource === 'yearly_reference' ? (
                        <span className="director-muted">Current value is a yearly reference, not an exact editable goal for this period yet.</span>
                      ) : null}
                      {existingGoal?.statusNote ? <span className="director-muted">Decision note: {existingGoal.statusNote}</span> : null}
                      {status.detail ? <span className="director-muted">{status.detail}</span> : null}
                      {editingGoalRow.isLocked ? (
                        <span className="director-warning-copy director-inline-note">Locked period. Unlock to edit.</span>
                      ) : isDirty ? (
                        <StatusPill tone="needs-link">Unsaved Change</StatusPill>
                      ) : (
                        <StatusPill tone="linked">Open</StatusPill>
                      )}
                    </div>
                  </div>
                </div>
                <div className="director-form-actions director-form-actions-spread">
                  <div className="director-table-actions">
                    {!readOnly ? (
                      <>
                        <Button
                          onClick={async () => {
                            const savedGoal = await handleSaveGoalRow(editingGoalRow);
                            if (savedGoal) {
                              setEditingGoalKpiKey('');
                            }
                          }}
                          loading={savingRowKey === editingGoalRow.kpiKey}
                          loadingLabel="Saving…"
                          disabled={editingGoalRow.isLocked}
                        >
                          {buttonLabel}
                        </Button>
                        {proposalEligible ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={async () => {
                              const savedGoal = await handleSaveGoalDraftRow(editingGoalRow);
                              if (savedGoal) {
                                setEditingGoalKpiKey('');
                              }
                            }}
                            loading={savingRowKey === `${editingGoalRow.kpiKey}:draft`}
                            loadingLabel="Saving Draft..."
                            disabled={editingGoalRow.isLocked || savingRowKey === editingGoalRow.kpiKey}
                          >
                            Save Draft
                          </Button>
                        ) : null}
                        {submissionEligible ? (
                          <Button
                            size="sm"
                            onClick={async () => {
                              const submittedGoal = await handleSubmitGoalForApproval(existingGoal);
                              if (submittedGoal) {
                                setEditingGoalKpiKey('');
                              }
                            }}
                            loading={savingRowKey === `${existingGoal.id}:submit`}
                            loadingLabel="Submitting..."
                          >
                            Submit for Approval
                          </Button>
                        ) : null}
                        {activationEligible ? (
                          <Button
                            size="sm"
                            onClick={async () => {
                              const activatedGoal = await handleActivateGoal(existingGoal);
                              if (activatedGoal) {
                                setEditingGoalKpiKey('');
                              }
                            }}
                            loading={savingRowKey === `${existingGoal.id}:activate`}
                            loadingLabel="Activating..."
                          >
                            Activate
                          </Button>
                        ) : null}
                        {removalEligible ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={async () => {
                              const removedGoal = await handleRemoveGoal(existingGoal);
                              if (removedGoal) {
                                setEditingGoalKpiKey('');
                              }
                            }}
                            loading={savingRowKey === `${existingGoal.id}:remove`}
                            loadingLabel="Removing…"
                          >
                            Remove
                          </Button>
                        ) : null}
                        {existingGoal ? (
                          <Button
                            size="sm"
                            variant={editingGoalRow.isLocked ? 'secondary' : 'danger'}
                            onClick={() => handleToggleLock(existingGoal)}
                            disabled={savingRowKey === editingGoalRow.kpiKey}
                          >
                            {editingGoalRow.isLocked ? 'Unlock' : 'Lock'}
                          </Button>
                        ) : null}
                      </>
                    ) : null}
                    {existingGoal ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setHistoryGoalId(existingGoal.id)}
                      >
                        History
                      </Button>
                    ) : null}
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setEditingGoalKpiKey('')}
                    disabled={savingRowKey === editingGoalRow.kpiKey}
                  >
                    Close
                  </Button>
                </div>
              </>
            );
          })()}
        </DirectorModal>
      ) : null}

      <div className="director-secondary-grid">
        <details className="director-card director-collapsible-card director-secondary-card">
          <summary className="director-collapsible-summary">
            <h2>Goal Register</h2>
            <span>{filteredGoals.length} records</span>
          </summary>
          <div className="director-collapsible-body">
            <DataTable
              tableClassName="director-goal-register-table"
              aria-label="Goal Register table, scroll horizontally for more columns"
              emptyMessage="No goal records match the current location and period context."
              isEmpty={!filteredGoals.length}
              columns={[
                { label: 'Location', className: 'director-goal-register-col-location' },
                { label: 'KPI' },
                { label: 'Period', className: 'director-goal-register-col-period' },
                { label: 'Goal', className: 'director-goal-register-col-goal' },
                { label: 'Goal state', className: 'director-goal-register-col-status' },
                { label: 'Actions', className: 'director-goal-register-col-actions' }
              ]}
            >
              {goalLocationGroups.map(group => (
                <React.Fragment key={group.key}>
                  {showGoalLocationHeaders ? (
                    <tr className="director-location-group-row">
                      <td colSpan="6">
                        <span>{group.label}</span>
                        <strong>{group.rows.length}</strong>
                      </td>
                    </tr>
                  ) : null}
                  {group.rows.map(goal => (
                    <tr key={goal.id}>
                      <td className="director-goal-register-col-location">{goal.locationCode}</td>
                      <td>{goal.label}</td>
                      <td className="director-goal-register-col-period">{formatGoalWindow(goal)}</td>
                      <td className="director-goal-register-col-goal">{goal.goalValue}</td>
                      <td className="director-goal-register-col-status">
                        <StatusPill tone={getGoalLifecycleBadge(goal).tone} title={getGoalLifecycleBadge(goal).detail}>
                          {getGoalLifecycleBadge(goal).label}
                        </StatusPill>
                        <div className="director-muted">{getGoalLifecycleBadge(goal).detail}</div>
                        {goal.statusNote ? <div className="director-muted">Decision note: {goal.statusNote}</div> : null}
                      </td>
                      <td className="director-goal-register-col-actions">
                        <div className="director-table-actions">
                          {!readOnly && ['draft', 'returned', 'rejected'].includes(String(goal.goalStatus || '').toLowerCase()) ? (
                            <Button size="sm" onClick={() => handleSubmitGoalForApproval(goal)}>
                              Submit
                            </Button>
                          ) : null}
                          {!readOnly && ['draft', 'approved'].includes(String(goal.goalStatus || '').toLowerCase()) ? (
                            <Button size="sm" onClick={() => handleActivateGoal(goal)}>
                              Activate
                            </Button>
                          ) : null}
                          {!readOnly && ['draft', 'approved', 'returned', 'rejected'].includes(String(goal.goalStatus || '').toLowerCase()) ? (
                            <Button size="sm" variant="secondary" onClick={() => handleRemoveGoal(goal)}>
                              Remove
                            </Button>
                          ) : null}
                          {!readOnly ? (
                            <Button size="sm" variant={goal.isLocked ? 'secondary' : 'danger'} onClick={() => handleToggleLock(goal)}>
                              {goal.isLocked ? 'Unlock' : 'Lock'}
                            </Button>
                          ) : null}
                          <Button size="sm" onClick={() => setHistoryGoalId(goal.id)}>
                            History
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </DataTable>
          </div>
        </details>

        <details className="director-card director-collapsible-card director-secondary-card">
          <summary className="director-collapsible-summary">
            <h2>Goal History</h2>
            <span>{selectedHistoryGoal ? selectedHistoryGoal.kpiKey : 'select a goal'}</span>
          </summary>
          <div className="director-collapsible-body">
            {selectedHistoryGoal ? (
              <div className="director-history-list">
                <div className="director-history-header">
                  <strong>{selectedHistoryGoal.locationCode} / {selectedHistoryGoal.label}</strong>
                  <Button size="sm" variant="secondary" onClick={() => setHistoryGoalId(null)}>
                    Close
                  </Button>
                </div>
                {goalHistory.length ? goalHistory.map(item => (
                  <div className="director-history-item" key={item.id}>
                    <div className="director-history-row">
                      <strong>{item.action}</strong>
                      <span>{new Date(item.changedAt).toLocaleString()}</span>
                    </div>
                    <div className="director-muted">
                      {item.oldValue !== null || item.newValue !== null
                        ? `Value: ${item.oldValue ?? 'n/a'} -> ${item.newValue ?? 'n/a'}`
                        : `Lock: ${String(item.oldLocked)} -> ${String(item.newLocked)}`}
                    </div>
                  </div>
                )) : <p className="director-muted">No history yet for this goal.</p>}
              </div>
            ) : (
              <p className="role-section-copy">Select a goal to review its history.</p>
            )}
          </div>
        </details>
      </div>
    </div>
  );
}

export default DirectorGoalsPanel;
